import { Builder } from '@builder.io/react';
import { S2IconsSection } from '../components/interactive-sections/S2IconsSection';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';
import { backgroundGradientOptions, iconColorOptions, textColorOptionsLimited } from '../utils/builderColorOptions';
import { getBackgroundColorInput, getBackgroundGradientInput, getPaddingInputs } from '../utils/builderSharedInputs';

Builder.registerComponent(S2IconsSection, {
    name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S2Icons,
    inputs: [
        {
            name: 'headline',
            type: 'string',
            defaultValue: 'Headline',
            // helperText: "Headline for the icons",
            required: true,
        },
        {
            name: 'headlineColor',
            type: 'string',
            defaultValue: 'Blue',
            friendlyName: 'Headline Color',
            enum: textColorOptionsLimited,
            // helperText: "Color of the headline text",
            required: false,
        },
        {
            name: 'description',
            type: 'string',
            defaultValue: 'Paragraph below the H2 headline',
            friendlyName: 'Text',
            helperText:
                'Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
            required: false,
        },
        {
            name: 'descriptionColor',
            type: 'string',
            defaultValue: 'Navy',
            friendlyName: 'Text Color',
            enum: textColorOptionsLimited,
            // helperText: "Color of the description text",
            required: false,
        },

        {
            name: 'icons',
            type: 'list',

            onChange: (options) => {
                // Check if the list has more than 8 items
                if (options.get('icons').length > 8) {
                    options.set('icons', options.get('icons').slice(0, 8));
                    alert('maximum items is 8, delete items to continue');
                }
                // check if the list has less than 3 items
                if (options.get('icons').length < 3) {
                    alert('minimum items is 3, add items to continue');
                }
            },
            subFields: [
                {
                    name: 'useLottie',
                    type: 'boolean',
                    defaultValue: false,
                    friendlyName: 'Use Lottie Animation',
                    helperText: 'Enable to use a Lottie animation instead of a static icon',
                },
                {
                    name: 'lottieUrl',
                    type: 'file',
                    allowedFileTypes: ['json'],
                    friendlyName: 'Lottie Animation File',
                    helperText: 'Upload Lottie JSON file. Only used if "Use Lottie Animation" is enabled.',
                    showIf: 'options.get("useLottie") === true',
                    required: true,
                },
                {
                    name: 'lottieLoop',
                    type: 'boolean',
                    defaultValue: false,
                    friendlyName: 'Loop Animation',
                    helperText: 'Whether the Lottie animation should loop continuously.',
                    showIf: 'options.get("useLottie") === true',
                },
                {
                    name: 'lottieLoopDelay',
                    type: 'number',
                    defaultValue: 0,
                    min: 0,
                    max: 60,
                    step: 0.1,
                    friendlyName: 'Loop Delay (seconds)',
                    helperText: 'Delay in seconds between loop iterations. Set to 0 for no delay. Max 60 seconds.',
                    showIf: 'options.get("useLottie") === true && options.get("lottieLoop") === true',
                    required: false,
                },
                {
                    name: 'src',
                    type: 'file',
                    allowedFileTypes: ['svg'],
                    defaultValue: '',
                    friendlyName: 'Icon',
                    helperText: 'Icon image (used when Lottie is disabled)',
                    showIf: 'options.get("useLottie") !== true',
                    required: true,
                },
                {
                    name: 'headline',
                    type: 'string',
                    defaultValue: 'Icon Headline',
                    friendlyName: 'Icon Subheadline',
                    helperText:
                        'Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
                    required: true,
                },
                {
                    name: 'description',
                    type: 'string',
                    defaultValue: 'Icon Description',
                    friendlyName: 'Icon Text',
                    helperText:
                        'Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
                    required: true,
                },
                {
                    name: 'href',
                    type: 'string',
                    defaultValue: '',
                    friendlyName: 'Link URL',
                    helperText:
                        "Optional link URL for the icon. For external sites, include the full URL with https:// (e.g., https://www.nytimes.com). For internal Milan pages, use relative paths (e.g., /process). If you don't want it to link to anything, leave it blank.",
                    required: false,
                },
            ],
            defaultValue: [
                {
                    id: 1,
                    src: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F2518cf05a70748f19a8fe359ecb91efd',
                    headline: 'Expert Care',
                    description: 'We perform over {{treatmentsPerMonth}} treatments each month.',
                    href: '',
                },
                {
                    id: 2,
                    src: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F2518cf05a70748f19a8fe359ecb91efd',
                    headline: 'Industry-Leading Experience',
                    description: 'All clinics have been medically supervised since 2012.',
                    href: '',
                },
                {
                    id: 3,
                    src: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F2518cf05a70748f19a8fe359ecb91efd',
                    headline: 'Convenient Locations',
                    description: "With {{clinicCount}} locations in {{stateCount}} states, there's a Milan near you.",
                    href: '',
                },
                {
                    id: 4,
                    src: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F2518cf05a70748f19a8fe359ecb91efd',
                    headline: 'Glowing Reviews',
                    description: '{{totalReviews}} 5-star reviews from happy Milan clients!',
                    href: '',
                },
            ],
            helperText: 'List of icons to display',
            required: true,
        },
        {
            name: 'iconColor',
            type: 'string',
            defaultValue: 'Navy',
            friendlyName: 'Icon Color',
            helperText: 'Color change will apply to SVG icons only',
            enum: iconColorOptions,
            required: false,
        },
        {
            name: 'cardHeadlineColor',
            type: 'string',
            defaultValue: 'Turquoise',
            friendlyName: 'Icon Subheadline Color',
            enum: textColorOptionsLimited,
            required: false,
        },
        {
            name: 'cardDescriptionColor',
            type: 'string',
            defaultValue: 'Navy',
            friendlyName: 'Icon Text Color',
            enum: textColorOptionsLimited,
            required: false,
        },
        ...getBackgroundColorInput({}),
        ...getBackgroundGradientInput({
            backgroundGradientName: 'gradient',
            defaultValue: 'BlueTop',
            backgroundGradientList: backgroundGradientOptions,
        }),
        ...getPaddingInputs(),
    ],
    friendlyName: 'S2 Icons',
});
